import { ArrowLeft, Monitor } from '@phosphor-icons/react';
import { Busy, Button, Heading, Icon, PinPad, Surface, Text } from '@shh/ui';
import { deleteUser, signInAnonymously, signOut } from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { pairDevice } from '../../data/api';
import { firebase } from '../../lib/firebase';
import { startText } from '../../i18n/start';

/**
 * Flow F8: the wall shows a big number pad, a parent creates the code on the phone
 * (Einstellungen → Geräte → Wanddisplay koppeln). No password on the shared screen.
 */
/** QR pairings of this page load, by code – see the effect in PairPage. */
const qrPairings = new Map<string, Promise<boolean>>();

export function PairPage() {
  const [error, setError] = useState<string>();
  const navigate = useNavigate();
  // the QR code from the parent's phone carries the code (1.29) – nothing to type
  const [params] = useSearchParams();
  const fromQr = /^\d{6}$/.test(params.get('code') ?? '') ? params.get('code')! : undefined;
  const [auto, setAuto] = useState(!!fromQr);

  /** Back to the login – an unpaired device login (anonymous) is removed on the way. */
  const back = async () => {
    const { auth } = firebase();
    const user = auth.currentUser;
    if (user?.isAnonymous) await deleteUser(user).catch(() => signOut(auth));
    void navigate('/', { replace: true });
  };

  const onCode = useCallback(async (code: string) => {
    setError(undefined);
    const { auth } = firebase();
    const created = !auth.currentUser;
    try {
      if (created) await signInAnonymously(auth);
      await pairDevice(code, startText.wallName);
      return true; // the session switches to the family view by itself
    } catch {
      // Don't leave an unpaired anonymous login behind – it would keep this screen forever.
      const user = auth.currentUser;
      if (created && user?.isAnonymous) await deleteUser(user).catch(() => signOut(auth));
      setError(startText.pairFailed);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!fromQr) return;
    // The anonymous sign-in re-renders the gate and mounts this page anew – the second page
    // waits for the same pairing instead of using the single-use code a second time.
    let attempt = qrPairings.get(fromQr);
    if (!attempt) {
      attempt = Promise.resolve(fromQr).then(onCode); // after the effect, not inside it
      qrPairings.set(fromQr, attempt);
    }
    let live = true;
    void attempt.then((ok) => {
      if (!ok && live) {
        setError(startText.pairFailed);
        setAuto(false); // wrong or expired: the pad with the hint, type a new one
      }
    });
    return () => {
      live = false;
    };
  }, [fromQr, onCode]);

  return (
    <main className="mx-auto flex min-h-dvh max-w-default flex-col justify-center gap-section px-safe py-section">
      <Button variant="quiet" icon={ArrowLeft} className="self-start" onPress={() => void back()}>
        {startText.toSignin}
      </Button>
      <div className="grid grid-cols-1 items-center gap-section md:grid-cols-2">
        <div className="flex flex-col gap-gap-lg">
          <span className="grid size-avatar-lg place-items-center rounded-card bg-accent-secondary-soft text-accent-secondary">
            <Icon icon={Monitor} size="xl" />
          </span>
          <Heading level={1} size="xl">
            {startText.setupWall}
          </Heading>
          <Text tone="secondary">{startText.setupWallIntro}</Text>
          <Surface tone="sunken" className="flex flex-col gap-gap-sm">
            <Text weight="semibold">{startText.howTo}</Text>
            <ol className="flex list-decimal flex-col gap-gap-xs pl-inset-md text-text-secondary">
              {startText.pairSteps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </Surface>
        </div>
        <div className="flex flex-col items-center gap-gap-md">
          {auto ? (
            <Busy label={startText.pairing} />
          ) : (
            <PinPad
              length={6}
              label={startText.pairCode}
              onComplete={onCode}
              error={error}
              checkingLabel={startText.pairing}
            />
          )}
        </div>
      </div>
    </main>
  );
}
